import React, { useEffect, useMemo, useState } from 'react'
import { useTable, useSortBy, useExpanded } from 'react-table'
import { formatCZK, getPriceColor } from './helpers/tableHelpers'
import DetailedRow from './DetailedRow'

const Analytics = ({ city, analyticsData, selectedDistricts, show }) => {
  const [data, setData] = useState([])
  const [minM2, setMinM2] = useState(0)
  const [maxM2, setMaxM2] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedRows, setExpandedRows] = useState({})

  useEffect(() => {
    const districts = analyticsData.districts || {}
    let rows = Object.entries(districts).map(([district, d]) => ({
      district,
      totalListings: d.totalListings,
      activeListings: d.activeListings,
      medianPrice: d.price?.median,
      medianM2Price: d.pricePerM2?.median,
      weightedM2Price: d.pricePerM2?.weightedMedian,
      avgSize: d.size?.average,
      medianSize: d.size?.median,
      timeToSell: d.timeOnMarket?.avg,
      medianTimeToSell: d.timeOnMarket?.median,
      completedSales: d.timeOnMarket?.count || 0,
      details: d,
    }))

    // Filter rows if districts are selected and show is not "full"
    if (selectedDistricts && selectedDistricts.length > 0 && show !== 'full') {
      rows = rows.filter((row) => selectedDistricts.some((district) => district.toLowerCase() === row.district.toLowerCase()))

      // If only one district is selected, expand it by default
      if (selectedDistricts.length === 1) {
        setExpandedRows({ 0: true })
      } else {
        setExpandedRows({})
      }
    } else {
      setExpandedRows({})
    }

    setData(rows)
    const m2Prices = rows.map((r) => r.medianM2Price).filter(Boolean)
    setMinM2(Math.min(...m2Prices))
    setMaxM2(Math.max(...m2Prices))
    setLoading(false)
  }, [analyticsData, selectedDistricts, show])

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data
    return data.filter((row) => row.district.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [data, searchTerm])

  const columns = useMemo(
    () => [
      {
        Header: 'Okres',
        accessor: 'district',
        Cell: ({ value }) => <div className="font-medium text-gray-900">{value}</div>,
      },
      {
        Header: 'Inzerátů',
        accessor: 'totalListings',
        Cell: ({ value, row }) => (
          <div className="text-center">
            <div className="font-medium">{value}</div>
            <div className="text-xs text-gray-500">{row.original.activeListings} aktivní</div>
          </div>
        ),
      },
      {
        Header: 'Kč/m²',
        accessor: 'medianM2Price',
        Cell: ({ value, row }) => (
          <div>
            <div className="font-medium" style={{ color: getPriceColor(value, minM2, maxM2) }}>
              {value ? value.toLocaleString('cs-CZ') + ' Kč/m²' : 'Na dotaz'}
            </div>
            {row.original.weightedM2Price && <div className="text-xs text-gray-500">Vážený: {row.original.weightedM2Price.toLocaleString()} Kč/m²</div>}
          </div>
        ),
      },
      {
        Header: 'Medián cena',
        accessor: 'medianPrice',
        Cell: ({ value }) => <div className="font-medium text-green-600">{formatCZK(value)}</div>,
      },
      {
        Header: 'Velikost',
        accessor: 'avgSize',
        Cell: ({ value, row }) => (
          <div>
            <div className="font-medium">{value ? Math.round(value) + ' m²' : ''}</div>
            {row.original.medianSize && <div className="text-xs text-gray-500">Medián: {row.original.medianSize} m²</div>}
          </div>
        ),
      },
      {
        Header: 'Doba prodeje',
        accessor: 'timeToSell',
        Cell: ({ value, row }) => (
          <div>
            <div className="font-medium">{value ? Math.round(value) + ' dní' : ''}</div>
            {row.original.medianTimeToSell && <div className="text-xs text-gray-500">Medián: {Math.round(row.original.medianTimeToSell)} dní</div>}
          </div>
        ),
      },
    ],
    [minM2, maxM2]
  )

  const hasSelectedDistricts = selectedDistricts && selectedDistricts.length > 0

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        sortBy: [{ id: 'totalListings', desc: true }],
        expanded: expandedRows,
      },
    },
    useSortBy,
    useExpanded
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, toggleAllRowsExpanded } = tableInstance

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Načítání dat...</div>
      </div>
    )
  }

  // Don't render anything if no districts are selected and show is not "full"
  if (!hasSelectedDistricts && show !== 'full') {
    return null
  }

  const toggleAllRows = () => {
    const allExpanded = rows.every((row) => row.isExpanded)
    setExpandedRows(allExpanded ? {} : rows.reduce((acc, _, index) => ({ ...acc, [index]: true }), {}))
    toggleAllRowsExpanded(!allExpanded)
  }

  return (
    <div className="space-y-4 px-4 md:px-6">
      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-100">
        {/* <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Statistiky okresů ({selectedDistricts.length})</h2>
          <button
            onClick={toggleAllRows}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {rows.every((row) => row.isExpanded) ? 'Sbalit vše' : 'Rozbalit vše'}
          </button>
        </div> */}
        <table {...getTableProps()} className="min-w-full">
          <thead className="bg-gray-50">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.render('Header')}</span>
                      <span className="text-gray-400">{column.isSorted ? (column.isSortedDesc ? '▼' : '▲') : '↕'}</span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
            {rows.map((row) => {
              prepareRow(row)
              return (
                <React.Fragment key={row.id}>
                  <tr
                    {...row.getRowProps()}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      const newExpanded = { ...expandedRows }
                      newExpanded[row.id] = !row.isExpanded
                      setExpandedRows(newExpanded)
                      row.toggleRowExpanded()
                    }}
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-900">
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                  {row.isExpanded && (
                    <tr>
                      <td colSpan={columns.length} className="p-0">
                        <DetailedRow data={row.original.details} city={city} district={row.original.district} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {filteredData.length === 0 && <div className="text-center py-8 text-gray-500">Nebyly nalezeny žádné okresy odpovídající vyhledávání.</div>}
    </div>
  )
}

export default Analytics
